import { Moment } from 'moment';
import React, { useEffect, useState } from 'react';
import { FinalButton } from '../../../../Components/FinalButton';
import { Checkbox, Input, MultiSelect, Select } from '../../../../Components/Form';
import { Option } from '../../../../Components/Form/Select/types';
import { InlineSVG } from '../../../../Components/Icons';
import { Text } from '../../../../globalStyles';
import { Nullable } from '../../../../types';
import { cx, TimeZone } from '../../../../utils';
import { AllocationType } from '../../util';
import * as Styles from '../Styles';
import { allocationTypeLabels, getMsBetween } from './availabilityUtils';
import { isCompletedAllocation, LocalAllocation } from './Hooks/types';
import { AllocError, EditAction } from './Hooks/useEditAvailability';
import { hasTimeType, TimeSelect } from './TimeSelect';
import { allocationTypes, AvailabilityProvider } from './types';

const ONE_HOUR_MS = 1000 * 60 * 60;

const mantraHealthOrgOption: Readonly<Option> = {
  id: -1,
  label: 'Mantra Health',
};

const typeOptions = allocationTypes.map(t => ({ id: t, label: allocationTypeLabels[t] }));

type AllocationProps = {
  date: Moment;
  allocation: LocalAllocation;
  timezone: TimeZone;
  provider: AvailabilityProvider;
  dispatch: React.Dispatch<EditAction>;
  error?: Nullable<AllocError>;
};

export const Allocation = ({
  date,
  allocation,
  timezone,
  provider,
  error,
  dispatch,
}: AllocationProps) => {
  const { id: allocationId, organizationId } = allocation;
  const [hoursExceedsDay, setHoursExceedDay] = useState(false);
  const [isEditingHours, setIsEditingHours] = useState(false);

  const msBetween = isCompletedAllocation(allocation)
    ? getMsBetween(allocation.startTime, allocation.endTime)
    : null;

  // "hoursInput" is a string since the user must be able to type decimal numbers into the input element
  const [hoursInput, setHoursInput] = useState(
    msBetween ? (msBetween / ONE_HOUR_MS).toString() : '0.0'
  );

  const canEditHours = hasTimeType(allocation, 'startTime') || hasTimeType(allocation, 'endTime');
  const hours = Number(hoursInput) || 0;

  /* Update hours from changes to time selections */
  useEffect(() => {
    if (!msBetween || isEditingHours) return;
    setHoursExceedDay(false);
    setHoursInput(error ? '0.0' : (msBetween / ONE_HOUR_MS).toString());
  }, [error, msBetween, isEditingHours]);

  /* Update time selections from changes to hours */
  useEffect(() => {
    // To prevent infinite loop
    const noChangeToHours = msBetween && msBetween / ONE_HOUR_MS === hours;

    if (hours <= 0 || !isEditingHours || noChangeToHours) {
      return;
    }

    // Even if the allocation is complete, as long as it has a startTime update
    if (hasTimeType(allocation, 'startTime')) {
      const endExceeds = endTimeExceedsDay(allocation, date, hours);
      if (!endExceeds) {
        dispatch({
          type: 'update',
          args: {
            allocationId,
            field: 'endTime',
            value: allocation.startTime.clone().add(hours * 60 * 60, 'seconds'),
          },
        });
      }
      setHoursExceedDay(endExceeds);
    }
    // Only update if the allocation does not have a startTime but has an endTime
    else if (hasTimeType(allocation, 'endTime')) {
      const startExceeds = startTimeExceedsDay(allocation, date, hours);
      if (!startExceeds) {
        dispatch({
          type: 'update',
          args: {
            allocationId,
            field: 'startTime',
            value: allocation.endTime.clone().subtract(hours * 60 * 60, 'seconds'),
          },
        });
      }
      setHoursExceedDay(startExceeds);
    }
  }, [
    hours,
    allocation,
    msBetween,
    allocationId,
    isEditingHours,
    date,
    dispatch,
    setHoursExceedDay,
  ]);

  const childrenOrgs = provider.organizations.find(i => i.id === organizationId)?.children ?? [];

  return (
    <>
      <Styles.AllocationGridV2>
        <TimeSelect
          className={cx({ error: error && error.field === 'startTime' })}
          forDate={date}
          timeType="startTime"
          timezone={timezone}
          allocation={allocation}
          onChange={(timeType, updated) => {
            dispatch({
              type: 'update',
              args: { allocationId, field: timeType, value: updated },
            });
          }}
        />
        <Styles.AllocationGridStatic>-</Styles.AllocationGridStatic>
        <TimeSelect
          className={cx({ error: error && error.field === 'endTime' })}
          forDate={date}
          timeType="endTime"
          timezone={timezone}
          allocation={allocation}
          onChange={(timeType, updated) => {
            dispatch({
              type: 'update',
              args: { allocationId, field: timeType, value: updated },
            });
          }}
        />
        <Input
          maxLength={5}
          value={hoursInput}
          disabled={!canEditHours}
          onChange={e => {
            if (isEditingHours) {
              setHoursInput(e.target.value);
            }
          }}
          onFocus={() => setIsEditingHours(true)}
          onBlur={() => setIsEditingHours(false)}
          style={{ width: 59 }}
        />
        <Select
          options={[
            mantraHealthOrgOption,
            ...provider.organizations
              .map(i => ({ id: i.id, label: i.name }))
              .sort((a, b) => a.label.localeCompare(b.label)),
          ]}
          value={organizationId ?? (mantraHealthOrgOption.id as number)}
          onChange={orgId =>
            dispatch({
              type: 'update',
              args: {
                allocationId,
                field: 'organizationId',
                value: orgId as number,
              },
            })
          }
          style={{ width: 200 }}
        />
        <Select
          options={typeOptions}
          value={typeOptions.find(i => i.id === allocation.type)?.id ?? typeOptions[0].id}
          onChange={allocType =>
            dispatch({
              type: 'update',
              args: {
                allocationId,
                field: 'type',
                value: allocType as AllocationType,
              },
            })
          }
          style={{ width: 170 }}
          clearable={false}
        />
        <Checkbox
          className="items-center justify-center"
          checked={allocation.isFeeForServiceTime ?? false}
          onChange={() =>
            dispatch({
              type: 'update',
              args: {
                allocationId,
                field: 'isFeeForServiceTime',
                value: !allocation.isFeeForServiceTime,
              },
            })
          }
          disabled={allocation.type === 'admin' || allocation.type === 'timeOff'}
        />
        <div className="flex justify-center items-center">
          <FinalButton
            className="db"
            size="tiny"
            kind="minimal_black"
            onClick={() => dispatch({ type: 'delete', args: { allocationId } })}
          >
            <InlineSVG icon="x" size={20} />
          </FinalButton>
        </div>
      </Styles.AllocationGridV2>
      {(childrenOrgs.length > 0 || hoursExceedsDay) && (
        <Styles.AllocationGridV2>
          {/* spacing to put it in the right part of the grid */}
          <div />
          <div />
          <div />
          {hoursExceedsDay ? <Text.caption kind="danger">Hours exceed day</Text.caption> : <div />}
          {childrenOrgs.length > 0 ? (
            <MultiSelect
              options={childrenOrgs
                .map(i => ({ id: i.id, label: i.name }))
                .sort((a, b) => a.label.localeCompare(b.label))}
              value={allocation.childOrganizationIds ?? []}
              onChange={childOrgIds =>
                dispatch({
                  type: 'update',
                  args: {
                    allocationId,
                    field: 'childOrganizationIds',
                    value: childOrgIds as number[],
                  },
                })
              }
              style={{ width: 200 }}
              placeholder="All campuses"
            />
          ) : (
            <div />
          )}
        </Styles.AllocationGridV2>
      )}
    </>
  );
};

const endTimeExceedsDay = (allocation: LocalAllocation, date: Moment, hours: number) => {
  const hasStart = hasTimeType(allocation, 'startTime');
  return (
    hasStart &&
    allocation.startTime
      .clone()
      .add(hours * 60 * 60, 'seconds')
      .isAfter(date.clone().endOf('day'))
  );
};

const startTimeExceedsDay = (allocation: LocalAllocation, date: Moment, hours: number) => {
  const hasEnd = hasTimeType(allocation, 'endTime');
  return (
    hasEnd &&
    allocation.endTime
      .clone()
      .subtract(hours * 60 * 60, 'seconds')
      .isBefore(date.clone().startOf('day'))
  );
};
